import dayjs from 'dayjs';
import classNames from 'classnames';
import { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { APP_ROUTES } from '@/constants';
import { BookingSessionViewModel, createUpcomingViewModel, removeTime } from '@/common/utils';
import { FeedbackModal } from '@/common/components/feedback-modal';
import { CenteredSpinner, Title } from '@/common/components';
import { useWaitingForFeedbackQuery } from '@/hooks/useWaitingForFeedbackQuery';
import useApplicationSettings from '@/hooks/useApplicationSettings';
import { RebuyModal } from '@/common/components/rebuy-modal';
import { DetailsSessionDialog } from '../details-session-dialog';
import { ReportSessionItem } from '../report-session-item';
import styles from './styles.module.scss';

type Props = {
  isAdvisor?: boolean;
};

export const FeedbackBlock = ({ isAdvisor }: Props) => {
  const { t } = useTranslation();
  const { appSettings } = useApplicationSettings();
  const [searchParams] = useSearchParams();
  const [selectedSession, setSelectedSession] = useState<BookingSessionViewModel>();
  const [dialogAction, setDialogAction] = useState<
    'view-session' | 'view-feedback' | 'view-rebuy'
  >();
  const today = removeTime(dayjs());
  const waitForFeedbackQuery = useWaitingForFeedbackQuery({
    fromDate: today,
    purpose: isAdvisor ? 'advisor-incompleted-feedbacks' : 'user-feedbacks',
    onSuccess(data) {
      if (searchParams.get('action') === 'write-session-feedback') {
        const bookingId = searchParams.get('id');

        if (bookingId) {
          // @ts-ignore
          const selectedSession = data?.items.find((s) => s.id.toString() === bookingId);
          if (selectedSession) {
            setDialogAction('view-feedback');
            setSelectedSession(createUpcomingViewModel(selectedSession, true));
          }
        }
      }
    },
  });

  const onCancel = useCallback(() => {
    setSelectedSession(undefined);
    setDialogAction(undefined);
  }, []);

  const data = useMemo(() => {
    if (!waitForFeedbackQuery.isFetched) {
      return [];
    }
    const arr = waitForFeedbackQuery.data?.items || [];

    return arr.map((s) => createUpcomingViewModel(s, true)) as BookingSessionViewModel[];
  }, [waitForFeedbackQuery.data?.items, waitForFeedbackQuery.isFetched]);

  let content = <div />;

  if (waitForFeedbackQuery.isLoading) {
    content = (
      <div style={{ height: 100 }}>
        <CenteredSpinner />
      </div>
    );
  } else {
    content = (
      <>
        {data.map((session: BookingSessionViewModel, index: number) => {
          return (
            <ReportSessionItem
              key={session.id}
              item={session}
              feedbackProps={{
                onFeedbackBtnClick: (s) => {
                  setDialogAction('view-feedback');
                  setSelectedSession(s);
                },
                feedbackStatus: session.feedbackStatus,
              }}
              className={classNames({
                [styles.firstItem]: index === 0,
              })}
              isAdvisor={isAdvisor || false}
              onRebuyBtnClick={(s) => {
                setDialogAction('view-rebuy');
                setSelectedSession(s);
              }}
            />
          );
        })}
      </>
    );
  }

  if (!appSettings.isFeedbackFeatureEnabled && isAdvisor) {
    return undefined;
  }

  if (!appSettings.isUserFeedbackFeatureEnabled && !isAdvisor) {
    return undefined;
  }

  if (waitForFeedbackQuery.isFetched && !waitForFeedbackQuery.data?.items?.length) {
    return undefined;
  }

  return (
    <div className={styles.wrapper}>
      <Title className={styles.title} type="default">
        {isAdvisor ? t('feedbackWidget.advisorTitle') : t('feedbackWidget.userTitle')}{' '}
        {data.length > 0 && <span>{`(${data.length})`}</span>}
      </Title>
      <div className={styles.bottomLine} />
      <div className={styles.contentWrapper}>{content}</div>
      {dialogAction === 'view-session' && selectedSession && (
        <DetailsSessionDialog
          session={selectedSession}
          onCancel={onCancel}
          isAdvisor={isAdvisor!}
        />
      )}
      {dialogAction === 'view-feedback' && selectedSession && (
        <FeedbackModal
          session={selectedSession}
          onCancel={() => {
            setSelectedSession(undefined);
            setDialogAction(undefined);
          }}
        />
      )}
      {dialogAction === 'view-rebuy' && selectedSession && (
        <RebuyModal
          session={selectedSession}
          onCancel={() => {
            setSelectedSession(undefined);
            setDialogAction(undefined);
          }}
        />
      )}
      <div className={styles.sessionHistoryWrapper}>
        <Link className={styles.link} to={APP_ROUTES.MY_FEEDBACKS}>
          {t('myFeedbacks.viewAllFeedbacks')}
        </Link>
      </div>
    </div>
  );
};
